import React, { createContext, useCallback, useContext, useState } from "react";
import { getDistinctOptions, DistinctData } from "../../services/genericService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import AlertOverlay from "../../components/Alert";

interface GenericContextProps {
  loading: boolean;
  error: string | null;
  distinctOptions: Record<string, DistinctData[]>;
  fetchDistinctOptions: (entity: string) => void;
}

interface GenericProviderProps {
  children: React.ReactNode;
}

const GenericContext = createContext<GenericContextProps | null>(null);

const GenericProvider: React.FC<GenericProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<{
    message: string;
    variant: "success" | "danger" | "warning" | "info";
  } | null>(null);

  const [distinctOptions, setDistinctOptions] = useState<Record<string, DistinctData[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDistinctOptions = useCallback(async (entity: string): Promise<DistinctData[]> => {
    setError(null);
    setLoading(true);

    try {
        const response: DistinctData[] = await getDistinctOptions(entity);

        setDistinctOptions((prev) => ({
            ...prev,
            [entity]: response,
        }));

        return response;
    } catch (err) {
        const errorMessage = getErrorMessage("fetchDistinctOptionsError", { entity });
        setError(errorMessage);
        setAlert({ message: errorMessage, variant: "danger" });

        console.error(`Erro ao buscar dados distintos para ${entity}:`, err);

        return [];
    } finally {
        setLoading(false);
    }
}, []);


  return (
    <GenericContext.Provider value={{ loading, error, distinctOptions, fetchDistinctOptions }}>
      {alert && (
        <AlertOverlay
          message={alert.message}
          variant={alert.variant}
          onClose={() => setAlert(null)}
        />
      )}

      <LoadingOverlay isLoading={loading} />
      {children}
    </GenericContext.Provider>
  );
};

export default GenericProvider;

export const useGeneric = () => {
  const context = useContext(GenericContext);
  if (!context) {
    throw new Error(getErrorMessage("wrongUseProviderError", 
      { 
        auth: useGeneric?.name, 
        providerName: GenericProvider?.name
      }
    ));
  }
  return context;
};